import memoize from 'lodash/memoize';
import * as Yup from 'yup';

// eslint-disable-next-line no-restricted-imports
import {
  GQAggregateKind,
  GQIntensityDenominatorKind,
} from '@watershed/shared-universal/generated/graphql-schema-types';

import intensityDenominatorsJson from '../generated/shared_data/intensityDenominatorsMetadata.json';
import assertIncluded from './assertIncluded';

/*
    The following is not business logic, but rather conversion from MARTA conventions
    to the GraphQL schema.
*/
export const intensityKindMapping: Record<
  GQIntensityDenominatorKind,
  IntensityDenominatorKind
> = {
  [GQIntensityDenominatorKind.Revenue]: 'revenue' as const,
  [GQIntensityDenominatorKind.Headcount]: 'headcount' as const,
  [GQIntensityDenominatorKind.NightsStayed]: 'nights_stayed' as const,
  [GQIntensityDenominatorKind.GrossProfit]: 'gross_profit' as const,
  [GQIntensityDenominatorKind.NetRevenue]: 'net_revenue' as const,
  [GQIntensityDenominatorKind.Megawatts]: 'megawatts' as const,
  [GQIntensityDenominatorKind.MonthlyActiveUsers]:
    'monthly_active_users' as const,
  [GQIntensityDenominatorKind.Orders]: 'orders' as const,
  [GQIntensityDenominatorKind.OrdersKg]: 'orders_kg' as const,
  [GQIntensityDenominatorKind.Merchants]: 'merchants' as const,
  [GQIntensityDenominatorKind.Patients]: 'patients' as const,
  [GQIntensityDenominatorKind.PayingSitesUnderManagement]:
    'paying_sites_under_management' as const,
  [GQIntensityDenominatorKind.OperationalRevenue]:
    'operational_revenue' as const,
  [GQIntensityDenominatorKind.Shipments]: 'shipments' as const,
  [GQIntensityDenominatorKind.Gmv]: 'gmv' as const,
  [GQIntensityDenominatorKind.Units]: 'units' as const,
  [GQIntensityDenominatorKind.SupplierSpend]: 'supplier_spend' as const,
};

export const aggregateKindMapping = {
  [GQIntensityDenominatorKind.Revenue]: GQAggregateKind.RevenueIntensity,
  [GQIntensityDenominatorKind.Headcount]: GQAggregateKind.HeadcountIntensity,
  [GQIntensityDenominatorKind.NightsStayed]: GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.GrossProfit]: GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.NetRevenue]: GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.Megawatts]: GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.MonthlyActiveUsers]:
    GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.Orders]: GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.OrdersKg]: GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.Merchants]: GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.Patients]: GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.PayingSitesUnderManagement]:
    GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.OperationalRevenue]:
    GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.Shipments]: GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.Gmv]: GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.Units]: GQAggregateKind.CustomIntensity,
  [GQIntensityDenominatorKind.SupplierSpend]: GQAggregateKind.CustomIntensity,
};

export const intensityKindStringToGqlMapping: Record<
  IntensityDenominatorKind,
  GQIntensityDenominatorKind
> = {
  revenue: 'Revenue',
  headcount: 'Headcount',
  nights_stayed: 'NightsStayed',
  gross_profit: 'GrossProfit',
  net_revenue: 'NetRevenue',
  megawatts: 'Megawatts',
  monthly_active_users: 'MonthlyActiveUsers',
  orders: 'Orders',
  orders_kg: 'OrdersKg',
  merchants: 'Merchants',
  patients: 'Patients',
  paying_sites_under_management: 'PayingSitesUnderManagement',
  operational_revenue: 'OperationalRevenue',
  shipments: 'Shipments',
  gmv: 'GMV',
  units: 'Units',
  supplier_spend: 'SupplierSpend',
};

export const MonetaryIntensityDenominatorKinds = [
  'revenue',
  'gross_profit',
  'net_revenue',
  'operational_revenue',
  'gmv',
  'supplier_spend',
] as const;

export type MonetaryIntensityDenominatorKind =
  (typeof MonetaryIntensityDenominatorKinds)[number];

export function isLegacyMonetaryIntensityDenominatorKind(
  intensityKind: string
): intensityKind is MonetaryIntensityDenominatorKind {
  return MonetaryIntensityDenominatorKinds.includes(
    intensityKind as MonetaryIntensityDenominatorKind
  );
}

export const CustomIntensityDenominatorKinds = [
  'nights_stayed',
  'megawatts',
  'monthly_active_users',
  'orders',
  'orders_kg',
  'merchants',
  'patients',
  'paying_sites_under_management',
  'shipments',
  'units',
] as const;

export type CustomIntensityDenominatorKind =
  (typeof CustomIntensityDenominatorKinds)[number];

export function isLegacyCustomIntensityDenominatorKind(
  intensityKind: string
): intensityKind is CustomIntensityDenominatorKind {
  return CustomIntensityDenominatorKinds.includes(
    intensityKind as CustomIntensityDenominatorKind
  );
}

/**
 * Intensity denominator kinds that are used in the intensity denominator artifact.
 *
 * Unless you're dealing with a physical BART schema or the IntensityDenominatorService which
 * still uses IntensityDenominatorKinds as its interface, use IntensityDenominatorKindsV2 instead.
 */
export const IntensityDenominatorKinds = [
  'headcount',
  ...MonetaryIntensityDenominatorKinds,
  ...CustomIntensityDenominatorKinds,
] as const;
export type IntensityDenominatorKind =
  (typeof IntensityDenominatorKinds)[number];

export const AggregationKinds = ['sum', 'average'] as const;
export type AggregationKind = (typeof AggregationKinds)[number];

export interface IntensityDenominatorMetadata {
  label: string;
  unit: string;
  unitUserFacing: string;
  intensityUnit: string;
  tooltipUnit: string;
  description: string;
  scaleFactor: number;
  aggregationKind?: AggregationKind;
  isCurrency: boolean;
}

/**
 * @deprecated Use IntensityDenominatorKindsV2 instead.
 */
export function stringToIntensityDenominatorKind(
  str: string
): IntensityDenominatorKind {
  assertIncluded(str, IntensityDenominatorKinds);
  return str;
}

const intensityDenominatorMetadataSchema = Yup.object({
  label: Yup.string().required(),
  // A specific reference to a value in the user_activity_table unit field
  unit: Yup.string().required(),
  // The most brief version of the unit
  unitUserFacing: Yup.string().required(),
  // The unit in the context of emissions
  intensityUnit: Yup.string().required(),
  // The brief version of intensityUnit
  tooltipUnit: Yup.string().required(),
  description: Yup.string().required(),
  scaleFactor: Yup.number().required(),
  // if calculating the intensity value over e.g. a year, should
  // you average or sum the monthly values (e.g. for headcount, you
  // would average the number of the time period but for revenue, you
  // would sum).
  aggregationKind: Yup.mixed()
    .oneOf([...AggregationKinds]) // have to spread otherwise the array is read-only
    .optional(),
  // True if the unit is a currency.
  isCurrency: Yup.boolean().required(),
})
  .defined()
  .noUnknown();

const intensityDenominatorsSchema: Record<
  IntensityDenominatorKind,
  typeof intensityDenominatorMetadataSchema
> = {
  revenue: intensityDenominatorMetadataSchema,
  headcount: intensityDenominatorMetadataSchema,
  nights_stayed: intensityDenominatorMetadataSchema,
  gross_profit: intensityDenominatorMetadataSchema,
  net_revenue: intensityDenominatorMetadataSchema,
  megawatts: intensityDenominatorMetadataSchema,
  monthly_active_users: intensityDenominatorMetadataSchema,
  orders: intensityDenominatorMetadataSchema,
  orders_kg: intensityDenominatorMetadataSchema,
  merchants: intensityDenominatorMetadataSchema,
  patients: intensityDenominatorMetadataSchema,
  paying_sites_under_management: intensityDenominatorMetadataSchema,
  operational_revenue: intensityDenominatorMetadataSchema,
  shipments: intensityDenominatorMetadataSchema,
  gmv: intensityDenominatorMetadataSchema,
  units: intensityDenominatorMetadataSchema,
  supplier_spend: intensityDenominatorMetadataSchema,
};

const intensityDenominatorsYupSchema = Yup.object(intensityDenominatorsSchema);

export const IntensityDenominatorKindsV2 = [
  'revenueQuantity',
  'grossProfitQuantity',
  'netRevenueQuantity',
  'operationalRevenueQuantity',
  'gmvQuantity',
  'supplierSpendQuantity',
  'nightsStayedQuantity',
  'megawattsQuantity',
  'monthlyActiveUsersQuantity',
  'ordersQuantity',
  'ordersKgQuantity',
  'merchantsQuantity',
  'patientsQuantity',
  'payingSitesUnderManagementQuantity',
  'shipmentsQuantity',
  'unitsQuantity',
  'employeeQuantity',
] as const;

export type IntensityDenominatorKindV2 =
  (typeof IntensityDenominatorKindsV2)[number];

const intensityDenominatorKindV2Mapping: Record<
  IntensityDenominatorKind,
  IntensityDenominatorKindV2
> = {
  revenue: 'revenueQuantity',
  gross_profit: 'grossProfitQuantity',
  net_revenue: 'netRevenueQuantity',
  operational_revenue: 'operationalRevenueQuantity',
  gmv: 'gmvQuantity',
  supplier_spend: 'supplierSpendQuantity',
  nights_stayed: 'nightsStayedQuantity',
  megawatts: 'megawattsQuantity',
  monthly_active_users: 'monthlyActiveUsersQuantity',
  orders: 'ordersQuantity',
  orders_kg: 'ordersKgQuantity',
  merchants: 'merchantsQuantity',
  patients: 'patientsQuantity',
  paying_sites_under_management: 'payingSitesUnderManagementQuantity',
  shipments: 'shipmentsQuantity',
  units: 'unitsQuantity',
  headcount: 'employeeQuantity',
};

/**
 * Map a legacy intensity denominator kind to the corresponding BART-based intensity denominator kind.
 * If the intensity kind is not found in the mapping, return the original string.
 */
export function getIntensityDenominatorKindV2(
  intensityKind: IntensityDenominatorKind
): IntensityDenominatorKindV2;
export function getIntensityDenominatorKindV2(intensityKind: string): string;
export function getIntensityDenominatorKindV2(intensityKind: string): string {
  const mapped =
    intensityDenominatorKindV2Mapping[
      intensityKind as keyof typeof intensityDenominatorKindV2Mapping
    ];

  return mapped ?? intensityKind;
}

/**
 * Returns a list of intensity denominators we're allowed to use, along with their units.
 */
export const getIntensityDenominators = memoize(
  (): Record<IntensityDenominatorKind, IntensityDenominatorMetadata> => {
    return intensityDenominatorsYupSchema.validateSync(
      intensityDenominatorsJson
    );
  }
);

/**
 * Check if a given string is a legacy intensity kind.
 */
export function isLegacyIntensityDenominatorKind(
  intensityKind: string
): intensityKind is IntensityDenominatorKind {
  return intensityKind in intensityDenominatorKindV2Mapping;
}

export default getIntensityDenominators();

export const DIVIDE_BY = {
  ...Object.fromEntries(
    Object.entries(getIntensityDenominators()).map(
      ([intensityKind, { scaleFactor }]) => [intensityKind, scaleFactor]
    )
  ),
  antiBriberyAndCorruptionCurrencyQuantity: 1_000_000,
  assetRevenueQuantity: 1_000_000,
  buildingAreaScaledQuantity: 1_000,
  gmvQuantity: 1_000_000,
  grossProfitQuantity: 1_000_000,
  lobbyingCurrencyQuantity: 1_000_000,
  merchantsQuantity: 1_000,
  monthlyActiveUsersQuantity: 1_000_000,
  netRevenueQuantity: 1_000_000,
  nightsStayedQuantity: 1_000_000,
  operationalRevenueQuantity: 1_000_000,
  ordersQuantity: 1_000,
  patientsQuantity: 1_000,
  payingSitesUnderManagementQuantity: 1_000,
  revenueQuantity: 1_000_000,
  severeHumanRightsIncidentCurrencyQuantity: 1_000_000,
  shipmentsQuantity: 1_000,
  supplierSpendQuantity: 1_000_000,
  unitsQuantity: 1_000,
  workersHoursWorkedQuantity: 1_000_000,
};
